import { useCallback, useEffect, useRef, useState } from "react";

/**
 * 인라인 편집이 서버에 반영되기를 기다리는 동안의 표시 (설계원칙 P20).
 *
 * 표에서 값을 고치면 요청이 나가고, 돌아오기 전까지 화면에는 **고친 값도 예전 값도 아닌 것**이
 * 잠깐 보인다. 저장이 끝났는지 아닌지 알 수 없으면 담당자는 같은 칸을 두 번 고치거나,
 * 반영되지 않은 값을 반영된 것으로 읽는다.
 *
 * 원칙 문서는 이 상태를 보여 줄 필요는 유효하지만 **색·형태에 대한 근거 있는 외부 스펙은
 * 없으니 조직 표준으로 정하고 문서화하라**고 한다. 이 시스템의 표준은 `index.css` 의
 * `.hj-pending` — 점선 밑줄이다. 색을 쓰지 않아 주색 규칙과 부딪히지 않는다.
 *
 * 해제 조건은 둘이다.
 * - `value` 가 바뀌어 돌아온다 (저장 성공, TanStack Query 가 무효화하고 다시 읽는다)
 * - 2초가 지난다 (서버가 거부했거나 값이 같아 응답이 화면을 바꾸지 않는 경우.
 *   실패 사유는 화면의 오류 문구가 따로 알린다 — 여기서 붙잡고 있으면 영영 점선으로 남는다)
 */
export function usePendingCommit<T>(value: T): {
  pending: boolean;
  markPending: () => void;
} {
  const [pending, setPending] = useState(false);
  /** 커밋을 누른 순간의 값 — 이것과 달라지면 서버가 반영한 것이다 */
  const committedFrom = useRef<T>(value);

  useEffect(() => {
    if (!pending) return;
    if (value !== committedFrom.current) {
      setPending(false);
      return;
    }
    const timer = window.setTimeout(() => setPending(false), 2000);
    return () => window.clearTimeout(timer);
  }, [value, pending]);

  const markPending = useCallback(() => {
    committedFrom.current = value;
    setPending(true);
  }, [value]);

  return { pending, markPending };
}
